import { EmailNotificationPreferences } from './EmailNotificationPreferences';
/**
 * The Requests mode: list on the left, the selected request on the right.
 *
 * The shell is the console's existing two-pane shape — the same list-pane and
 * content-column anatomy Shared Docs uses, two independent scrollers, the app
 * shell itself never scrolling. Nothing here is new chrome; the right-hand pane
 * is `FeedbackRequestPage` unchanged.
 *
 * Selection is the URL. A row links to `/org/:orgSlug/feedback/:requestId`, and
 * a pasted link arriving cold at that same URL selects the row — one code path,
 * so a deep link cannot drift from a click.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { EditorBreadcrumb, EditorHeaderBar } from '@nimbalyst/collab-bundle/docs-ui';

import type { TeamMember, TeamSummary } from '../api';
import type { TeamJwt } from '../auth/teamJwt';
import { getOrgRoster, loadOrgRoster, memberDisplayName } from '../collab/orgRoster';
import { DecorativeIcon } from '../organization/DecorativeIcon';
import { orgFeedbackRequestPath, orgRequestActivityPath } from '../routing';
import { useRequestsInbox } from './RequestsInboxContext';
import {
  requestDocumentProject,
  formatRequestTimestamp,
  groupRequestRows,
  REQUESTS_FILTERS,
  selectRequestRows,
  type RequestRow,
  type RequestsFilterId,
  type ResolveActorName,
} from './requestsInboxModel';

type GetTeamJwt = (options?: { forceRefresh?: boolean }) => Promise<TeamJwt>;

/**
 * The activity pills. `primary` is what is being asked of the reader, `muted` is
 * context, `agentic` marks a request an agent raised rather than a person.
 */
const PILL_BASE = 'inline-flex items-center whitespace-nowrap rounded-full px-[7px] py-0.5 text-[0.66rem] font-semibold';
const PILL_TONE = {
  primary: 'text-nim-primary bg-[color-mix(in_srgb,var(--nim-primary)_14%,transparent)]',
  muted: 'text-nim-muted bg-nim-tertiary',
  agentic: 'text-[color:var(--nim-purple)] bg-[color-mix(in_srgb,var(--nim-purple)_16%,transparent)]',
} as const;

function Pill({ tone, children }: { tone: keyof typeof PILL_TONE; children: ReactNode }) {
  return <span className={`requests-pill ${PILL_BASE} ${PILL_TONE[tone]}`}>{children}</span>;
}

/** The empty-state glyph, shared by the pane and the unselected content column. */
function RequestsGlyph() {
  return (
    <span className="requests-empty-glyph mb-2.5 grid size-[42px] place-items-center rounded-[10px] bg-nim-tertiary text-nim-faint">
      <DecorativeIcon icon="ballot" size={22} />
    </span>
  );
}

function RequestListRow({
  row,
  team,
  now,
  selected,
  onDismiss,
}: {
  row: RequestRow;
  team: TeamSummary;
  now: number;
  selected: boolean;
  onDismiss: (deliveryId: string) => void;
}) {
  const content = (
    <>
      <span className="requests-row-head flex items-baseline gap-[7px]">
        <span className="requests-row-who min-w-0 truncate text-[0.78rem] font-semibold text-nim">
          {row.askedBy || 'Someone in this organization'}
        </span>
        <span className="requests-row-when ml-auto shrink-0 text-[0.66rem] text-nim-faint">
          {formatRequestTimestamp(row.createdAt, now)}
        </span>
      </span>
      {row.question && (
        <span
          className={`requests-row-question line-clamp-2 select-text text-[0.78rem] leading-[1.45] ${row.unread ? 'text-nim' : 'text-nim-muted'}`}
        >
          {row.question}
        </span>
      )}
      <span className="requests-row-meta mt-1 flex flex-wrap items-center gap-1.5">
        {row.sourceKind === 'feedbackRequest'
          ? row.unread
            ? <Pill tone="primary">Waiting on you</Pill>
            : <Pill tone="muted">Opened</Pill>
          : <Pill tone="muted">{row.kindLabel}</Pill>}
        {row.sourceKind !== 'feedbackRequest' && <Pill tone="primary">{row.activityLabel}</Pill>}
        {row.viaAgent && <Pill tone="agentic">via agent</Pill>}
      </span>
    </>
  );
  const link = 'requests-row-link grid gap-0.5 py-[9px] pl-3 pr-[30px] text-inherit no-underline';
  return (
    <div
      className={`requests-row group relative rounded-md hover:bg-nim-hover ${selected ? 'bg-nim-selected' : ''}`}
      data-unread={row.unread ? 'true' : undefined}
    >
      {/* The unread rail sits in the row's gutter, so the row's own text does
          not have to shift to make room for it. */}
      {row.unread && (
        <span
          className="absolute left-1 top-[15px] size-[5px] rounded-full bg-nim-primary"
          aria-hidden="true"
        />
      )}
      {row.requestId
        ? (
          <Link
            className={link}
            to={orgFeedbackRequestPath(team, row.requestId)}
            aria-current={selected ? 'page' : undefined}
          >
            {content}
          </Link>
        )
        : row.documentTarget && requestDocumentProject(team, row)
          ? (
            <Link
              className={link}
              to={orgRequestActivityPath(team, row.deliveryId, row.documentTarget)}
              aria-current={selected ? 'page' : undefined}
            >
              {content}
            </Link>
          )
          : <div className={link}>{content}<span className="requests-row-unavailable text-xs text-nim-faint">This activity is available in the desktop app.</span></div>}
      <button
        className="requests-row-dismiss absolute right-1.5 top-[7px] grid size-[22px] cursor-pointer place-items-center rounded-[5px] border-none bg-transparent text-nim-faint opacity-0 hover:bg-nim-hover hover:text-nim focus-visible:opacity-100 group-hover:opacity-100"
        type="button"
        title="Dismiss this activity"
        aria-label={`Dismiss activity from ${row.askedBy || 'this organization'}`}
        onClick={() => onDismiss(row.deliveryId)}
      >
        <DecorativeIcon icon="close" size={15} />
      </button>
    </div>
  );
}

/**
 * Deliberately without a call to action.
 *
 * Requests arrive from someone in the organization; there is nothing here to
 * configure and no way to go and find one. Copy that offered a next step would
 * be inventing one on the screen whose whole job is to be empty most of the time.
 */
function RequestsEmpty({ orgName }: { orgName: string }) {
  return (
    <div className={`requests-empty ${EMPTY_COLUMN}`} data-requests-state="empty">
      <RequestsGlyph />
      <strong className={EMPTY_TITLE}>Nothing waiting on you</strong>
      <p className={EMPTY_BODY}>Feedback requests, mentions, and replies from {orgName} show up here.</p>
    </div>
  );
}

const EMPTY_COLUMN = 'flex h-full flex-col items-center justify-center gap-[3px] px-[1.6rem] py-8 text-center';
const EMPTY_TITLE = 'text-[0.86rem] font-semibold text-nim';
const EMPTY_BODY = 'mt-1 max-w-[34ch] text-[0.76rem] leading-relaxed text-nim-faint';

/**
 * Names for the delivery authors.
 *
 * A feedback delivery carries a structured `Actor` of ids and no preview label,
 * so without this every row reads as an anonymous stranger. The roster is the
 * one the console already loads for document comments — single-flight and
 * cached per org, so entering Requests from Shared Docs costs no extra request.
 */
function useActorNames(orgId: string, getTeamJwt: GetTeamJwt): ResolveActorName {
  const [roster, setRoster] = useState<readonly TeamMember[]>(() => getOrgRoster(orgId));

  useEffect(() => {
    let cancelled = false;
    setRoster(getOrgRoster(orgId));
    loadOrgRoster(orgId, getTeamJwt).then((members) => {
      if (!cancelled) setRoster(members);
    }).catch(() => {
      // A roster this reader may not list is not an error for this surface;
      // rows fall back to unattributed rather than failing.
    });
    return () => { cancelled = true; };
  }, [getTeamJwt, orgId]);

  return useMemo(() => {
    const namesById = new Map(roster.map((member) => [member.memberId, memberDisplayName(member)]));
    return (userId: string) => namesById.get(userId);
  }, [roster]);
}

export function RequestsShell({
  team,
  getTeamJwt,
  filter,
  onFilterChange,
  selectedRequestId,
  selectedDeliveryId,
  now = Date.now(),
  children,
}: {
  team: TeamSummary;
  getTeamJwt: GetTeamJwt;
  filter: RequestsFilterId;
  onFilterChange: (filter: RequestsFilterId) => void;
  /** From the URL. Present for a deep link too, which is the point. */
  selectedRequestId?: string;
  /** From the URL: the activity row whose document the content column shows. */
  selectedDeliveryId?: string;
  now?: number;
  children: ReactNode;
}) {
  const { snapshot, markRead, markAllRead, dismiss } = useRequestsInbox();
  const resolveActorName = useActorNames(team.orgId, getTeamJwt);
  const { rows, unreadCount, totalCount, filterCounts } = useMemo(
    () => selectRequestRows(snapshot.deliveries, filter, { resolveActorName }),
    [filter, resolveActorName, snapshot.deliveries],
  );
  const groups = useMemo(() => groupRequestRows(rows, now), [now, rows]);
  // Deliberately over the unfiltered set, not `rows`: a deep link may name a
  // row the active filter is hiding, and it still has to be marked read.
  const deliveryToRead = useMemo(() => {
    const all = selectRequestRows(snapshot.deliveries).rows;
    const selected = selectedDeliveryId
      ? all.find((row) => row.deliveryId === selectedDeliveryId)
      : selectedRequestId
        ? all.find((row) => row.requestId === selectedRequestId)
        : undefined;
    return selected?.unread ? selected.deliveryId : undefined;
  }, [selectedDeliveryId, selectedRequestId, snapshot.deliveries]);

  // Reading a row is what consumes its delivery. Keyed on the delivery id,
  // which the lookup above only yields while the row is still unread, so this
  // fires once per selection rather than on every snapshot.
  useEffect(() => {
    if (deliveryToRead) markRead([deliveryToRead]);
  }, [deliveryToRead, markRead]);

  return (
    // The grid template steps down on a narrow shell; that override is a real
    // `@container` at-rule in `styles.css`. `data-request-open` is what those
    // rules key the phone layout off: a feedback-request link is the console's
    // most likely cold entry point on a phone, and at that width the request
    // takes the viewport rather than sharing it with a list the reader did not
    // come for.
    <div
      className="requests-shell grid h-full min-h-0 w-full overflow-hidden bg-nim"
      data-request-open={selectedRequestId || selectedDeliveryId ? 'true' : 'false'}
    >
      <aside className="requests-pane flex min-w-0 min-h-0 flex-col overflow-hidden border-r border-nim bg-nim-secondary" aria-label="Requests and activity">
        <header className="requests-pane-head flex h-11 shrink-0 items-center gap-2 border-b border-nim pl-[13px] pr-2">
          <h2 className="m-0 flex-1 text-[0.9rem] font-bold">Requests</h2>
          {unreadCount > 0 && (
            <button
              className="requests-pane-action grid size-[26px] shrink-0 cursor-pointer place-items-center rounded-[5px] border-none bg-transparent text-nim-faint hover:bg-nim-hover hover:text-nim"
              type="button"
              title="Mark all requests read"
              aria-label="Mark all requests read"
              onClick={markAllRead}
            >
              <DecorativeIcon icon="done_all" size={17} />
            </button>
          )}
        </header>
        {/* Where this surface grows. Comments and mentions become chips beside
            these when their context panes land — not new gutter items. */}
        <div className="requests-filters flex shrink-0 items-center gap-[5px] border-b border-nim px-2.5 py-2" role="group" aria-label="Filter requests">
          {REQUESTS_FILTERS.map(({ id, label }) => (
            <button
              className={`requests-chip inline-flex cursor-pointer items-center gap-[5px] whitespace-nowrap rounded-full border px-[9px] py-[3px] text-[0.72rem] font-medium ${
                filter === id
                  ? 'border-nim-primary bg-nim-selected text-nim'
                  : 'border-nim bg-transparent text-nim-muted hover:bg-nim-hover hover:text-nim'
              }`}
              key={id}
              type="button"
              aria-pressed={filter === id}
              onClick={() => onFilterChange(id)}
            >
              {label}
              {filterCounts[id] > 0 && (
                <span className="requests-chip-count font-bold text-nim-primary">{filterCounts[id]}</span>
              )}
            </button>
          ))}
        </div>
        <div className="requests-pane-scroll min-h-0 flex-1 overflow-y-auto p-1.5">
          {rows.length === 0
            ? totalCount > 0
              ? (
                <div className={`requests-empty ${EMPTY_COLUMN}`} data-requests-state="filtered">
                  <strong className={EMPTY_TITLE}>Nothing in this filter</strong>
                  <p className={EMPTY_BODY}>Choose another chip to see the rest of your activity.</p>
                </div>
              )
              : <RequestsEmpty orgName={team.name} />
            : groups.map((group) => (
              <section className="requests-group" key={group.id}>
                <h3 className="m-0 px-2 pb-1 pt-2.5 text-[0.62rem] font-semibold uppercase tracking-[0.08em] text-nim-faint">
                  {group.label}
                </h3>
                {group.rows.map((row) => (
                  <RequestListRow
                    key={row.deliveryId}
                    row={row}
                    team={team}
                    now={now}
                    selected={(!!row.requestId && row.requestId === selectedRequestId) || row.deliveryId === selectedDeliveryId}
                    onDismiss={dismiss}
                  />
                ))}
              </section>
            ))}
        </div>
        <EmailNotificationPreferences key={team.orgId} orgId={team.orgId} getTeamJwt={getTeamJwt} />
      </aside>
      <div className="requests-content flex min-w-0 min-h-0 flex-col overflow-hidden [&>*:only-child]:h-full [&>*:only-child]:min-h-0">
        {children}
      </div>
    </div>
  );
}

/** What the content column shows before a request is chosen. */
export function RequestsUnselected({ orgName }: { orgName: string }) {
  return (
    <section className="requests-unselected flex min-w-0 min-h-0 flex-col overflow-hidden">
      <EditorHeaderBar
        breadcrumb={(
          <EditorBreadcrumb
            crumbs={[
              { id: 'org', label: orgName },
              { id: 'requests', label: 'Requests', current: true },
            ]}
          />
        )}
      />
      <div className={`requests-unselected-body min-h-0 flex-1 overflow-y-auto ${EMPTY_COLUMN}`}>
        <RequestsGlyph />
        <strong className={EMPTY_TITLE}>Select a request</strong>
        <p className={EMPTY_BODY}>Choose a feedback request on the left to read and answer it.</p>
      </div>
    </section>
  );
}
